import { Link } from "react-router-dom";

// A small Markdown renderer for the legal pages (/terms, /privacy, /refunds): headings, paragraphs, lists,
// tables, quotes and rules, with **bold**, *italic*, `code` and [links](...) inside them. It builds React
// elements only (the text never becomes HTML), and links go only to this site's pages, https or mailto.

const LIST_ITEM = /^(\s*)([-*]|\d+\.)\s+(.*)$/;
const HEADING = /^(#{1,6})\s+(.*)$/;
const RULE = /^-{3,}\s*$/;
const TABLE_RULE = /^\|?\s*:?-{3,}/;

export function slug(text) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function startsBlock(line) {
  return HEADING.test(line) || RULE.test(line) || line.startsWith(">") || line.startsWith("|") || LIST_ITEM.test(line);
}

function cells(line) {
  return line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
}

// A list whose items start at `indent` spaces; deeper items become a nested list.
function parseList(lines, start, indent) {
  const ordered = /\d/.test(lines[start].match(LIST_ITEM)[2]);
  const items = [];
  let i = start;
  while (i < lines.length) {
    const m = lines[i].match(LIST_ITEM);
    if (!m || m[1].length !== indent) break;
    const item = { text: m[3], children: null };
    i += 1;
    while (i < lines.length && lines[i].trim()) {
      const n = lines[i].match(LIST_ITEM);
      if (n && n[1].length > indent) {
        [item.children, i] = parseList(lines, i, n[1].length);
      } else if (n || (!/^\s/.test(lines[i]) && startsBlock(lines[i]))) {
        break;
      } else {
        item.text += " " + lines[i].trim();  // the item's text goes on
        i += 1;
      }
    }
    items.push(item);
    // a blank line followed by another item of this list doesn't end it
    let j = i;
    while (j < lines.length && !lines[j].trim()) j += 1;
    const next = j < lines.length && lines[j].match(LIST_ITEM);
    if (j === i || !next || next[1].length !== indent) break;
    i = j;
  }
  return [{ type: "list", ordered, items }, i];
}

export function parseMarkdown(markdown) {
  const lines = String(markdown || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i += 1; continue; }
    const heading = line.match(HEADING);
    if (heading) {
      blocks.push({ type: "heading", level: heading[1].length, text: heading[2], id: slug(heading[2]) });
      i += 1;
    } else if (RULE.test(line)) {
      blocks.push({ type: "rule" });
      i += 1;
    } else if (line.startsWith(">")) {
      const quote = [];
      while (i < lines.length && lines[i].startsWith(">")) quote.push(lines[i++].replace(/^>\s?/, ""));
      blocks.push({ type: "quote", text: quote.join(" ").trim() });
    } else if (line.startsWith("|")) {
      const rows = [];
      while (i < lines.length && lines[i].startsWith("|")) rows.push(lines[i++]);
      const body = rows.slice(1).filter((r) => !TABLE_RULE.test(r));
      blocks.push({ type: "table", head: cells(rows[0]), rows: body.map(cells) });
    } else if (LIST_ITEM.test(line)) {
      const [list, next] = parseList(lines, i, line.match(LIST_ITEM)[1].length);
      blocks.push(list);
      i = next;
    } else {
      const text = [];
      while (i < lines.length && lines[i].trim() && (!text.length || !startsBlock(lines[i]))) text.push(lines[i++].trim());
      blocks.push({ type: "paragraph", text: text.join(" ") });
    }
  }
  return blocks;
}

const INLINE = /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;

function MdLink({ href, children }) {
  const cls = "underline underline-offset-2 hover:text-foreground";
  if (href.startsWith("/") && !href.startsWith("//")) return <Link to={href} className={cls}>{children}</Link>;
  if (href.startsWith("#")) return <a href={href} className={cls}>{children}</a>;
  if (/^(https:|mailto:)/i.test(href)) return <a href={href} className={cls} target="_blank" rel="noopener noreferrer">{children}</a>;
  return children;  // anything else (http:, javascript:, ...) stays text
}

export function MdInline({ text }) {
  return String(text || "").split(INLINE).map((part, i) => {
    if (i % 2 === 0) return part;
    if (part.startsWith("**")) return <strong key={i} className="font-semibold text-foreground"><MdInline text={part.slice(2, -2)} /></strong>;
    if (part.startsWith("`")) return <code key={i} className="font-mono text-[0.9em] px-1 rounded bg-muted">{part.slice(1, -1)}</code>;
    if (part.startsWith("*")) return <em key={i}>{part.slice(1, -1)}</em>;
    const split = part.indexOf("](");
    return <MdLink key={i} href={part.slice(split + 2, -1)}>{part.slice(1, split)}</MdLink>;
  });
}

function MdList({ list }) {
  const Tag = list.ordered ? "ol" : "ul";
  return (
    <Tag className={`${list.ordered ? "list-decimal" : "list-disc"} pl-6 my-3 space-y-1.5 marker:text-muted-foreground`}>
      {list.items.map((item, i) => (
        <li key={i} className="pl-1"><MdInline text={item.text} />{item.children && <MdList list={item.children} />}</li>
      ))}
    </Tag>
  );
}

// Graphite: h1 (28px on phones), then h4 and h5 in the display face
export const HEADING_CLASS = {
  1: "font-display font-semibold text-[20px] leading-[1.2] tracking-[0.02em] sm:text-h1 mb-6",
  2: "font-display font-semibold text-h4 mt-12 mb-3",
  3: "font-display font-semibold text-lg leading-normal tracking-[0.02em] mt-8 mb-2",
};

function Block({ block }) {
  switch (block.type) {
    case "heading": {
      const Tag = `h${block.level}`;
      return <Tag id={block.id} className={`${HEADING_CLASS[block.level] || HEADING_CLASS[3]} scroll-mt-6 text-foreground`}><MdInline text={block.text} /></Tag>;
    }
    case "rule":
      return <hr className="my-8 border-border" />;
    case "quote":
      return <blockquote className="border-l-2 pl-4 my-4"><MdInline text={block.text} /></blockquote>;
    case "list":
      return <MdList list={block} />;
    case "table":
      return (
        <div className="my-4 overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-card">
              <tr>{block.head.map((c, i) => <th key={i} className="text-left font-medium text-foreground px-3 py-2 border-b"><MdInline text={c} /></th>)}</tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r} className="border-b last:border-0">
                  {row.map((c, i) => <td key={i} className="px-3 py-2 align-top"><MdInline text={c} /></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return <p className="my-3"><MdInline text={block.text} /></p>;
  }
}

export function Markdown({ text }) {
  return parseMarkdown(text).map((block, i) => <Block key={i} block={block} />);
}
